import type { ComponentPropsWithoutRef } from 'react';

// Contrast and focus visibility are INV-13 requirements, so they are in the one place a button is
// defined rather than repeated per screen. Disabled is used only for "this action is running" —
// CB-REQ ADO-06 prohibits a disabled control standing in for a capability that does not exist yet.
//
// Modernist (adopted 2026-09-19, rethemed 2026-09-30): rounded, a Sora label, and the label sits
// flush left even when the button is wider than it. Primary is the solid blue; quiet is the outlined
// secondary. Neither is ever red: a destructive action says what it does in its label instead.
//
// The fill is accent-600 with white text, 5.43:1 (INV-13 asks 4.5:1 of 14px text, however heavy).

interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  readonly variant?: 'primary' | 'quiet';
}

export function Button({ variant = 'primary', className = '', ...button }: ButtonProps) {
  const base =
    'inline-flex items-center justify-start gap-1.5 rounded-md border px-[14px] py-2 font-heading ' +
    'text-sm font-bold ' +
    'leading-tight cursor-pointer disabled:cursor-not-allowed disabled:opacity-45';
  const look =
    variant === 'primary'
      ? 'border-transparent bg-accent-600 text-white hover:bg-accent-700 active:bg-accent-800'
      : 'border-divider bg-transparent text-ink hover:bg-ink/7 active:bg-ink/14';
  return <button className={`${base} ${look} ${className}`} {...button} />;
}
